import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import TableScroll from "../../components/TableScroll";
import { cloneAssessmentToDevice, fetchDeviceAssessment } from "../../services/assessmentApi";
import { fetchBrands } from "../../services/brandApi";
import {
  createDevice,
  deleteDevice,
  fetchCategories,
  fetchDeviceById,
  fetchDevicesByBrand,
  fetchSeries,
  createSeries,
  searchDevices,
  toggleDevicePopular,
  updateDevice,
} from "../../services/deviceApi";
import StorageVariantsEditor from "../../components/admin/StorageVariantsEditor";
import {
  formatStorageSummary,
  storageRowsFromApi,
  storageRowsToMap,
} from "../../utils/assessmentFormat";
import "../../styles/admin-common.css";

const emptyForm = {
  brandId: "",
  categoryId: "",
  seriesId: "",
  newSeriesName: "",
  cloneFromDeviceId: "",
  modelName: "",
  imageUrl: "",
  basePrice: "",
  launchDate: "",
  storageRows: [{ storage: "128", price: "0" }, { storage: "256", price: "5000" }],
};

const Models = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [assessmentPreview, setAssessmentPreview] = useState(null);
  const [cloneTarget, setCloneTarget] = useState(null);
  const [cloneSourceId, setCloneSourceId] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);

  useEffect(() => {
    fetchBrands().then(setBrands).catch(() => {});
    fetchCategories().then(setCategories).catch(() => {});
    searchDevices({ query: "", size: 300 }).then((d) => setAllDevices(d.content || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const catId = searchParams.get("categoryId");
    const brandId = searchParams.get("brandId");
    if (catId) {
      setCategoryFilter(catId);
      setForm((prev) => ({ ...prev, categoryId: catId }));
      setPage(0);
    }
    if (brandId) {
      setBrandFilter(brandId);
      setForm((prev) => ({ ...prev, brandId }));
      setPage(0);
    }
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadDevices = async () => {
    setLoading(true);
    setError("");
    try {
      let data;
      if (brandFilter && categoryFilter) {
        data = await fetchDevicesByBrand(brandFilter, categoryFilter, page, 10);
      } else {
        // Empty query → backend LIKE '%%' (all active). Never use a lone space —
        // that accidentally hides models whose names/brands have no spaces.
        data = await searchDevices({
          query: debouncedSearch,
          brandId: brandFilter || undefined,
          page,
          size: 10,
        });
      }
      let list = data.content || [];
      if (categoryFilter && !brandFilter) {
        list = list.filter((d) => String(d.categoryId) === String(categoryFilter));
      }
      setDevices(list);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, brandFilter, categoryFilter, page]);

  useEffect(() => {
    if (!form.brandId || !form.categoryId) {
      setSeriesOptions([]);
      return;
    }
    setSeriesLoading(true);
    fetchSeries(form.brandId, form.categoryId)
      .then(setSeriesOptions)
      .catch(() => setSeriesOptions([]))
      .finally(() => setSeriesLoading(false));
  }, [form.brandId, form.categoryId]);

  const parseStorage = (rows) => storageRowsToMap(rows);

  const buildPayload = async () => {
    const brandId = parseInt(form.brandId, 10);
    const categoryId = form.categoryId ? parseInt(form.categoryId, 10) : undefined;
    const basePrice = parseFloat(form.basePrice);
    if (!Number.isFinite(brandId)) {
      throw new Error("Select a brand");
    }
    if (!Number.isFinite(categoryId)) {
      throw new Error("Select a product category");
    }
    if (!Number.isFinite(basePrice)) {
      throw new Error("Enter a valid base price");
    }

    let seriesId = null;
    const newSeriesName = form.newSeriesName?.trim();
    if (newSeriesName) {
      const created = await createSeries({
        name: newSeriesName,
        brandId,
        categoryId,
      });
      seriesId = created.id;
    } else if (form.seriesId) {
      seriesId = parseInt(form.seriesId, 10);
      if (!Number.isFinite(seriesId)) {
        seriesId = null;
      }
    }

    const cloneFrom = form.cloneFromDeviceId
      ? parseInt(form.cloneFromDeviceId, 10)
      : undefined;
    return {
      brandId,
      categoryId,
      seriesId,
      cloneAssessmentFromDeviceId: Number.isFinite(cloneFrom) ? cloneFrom : undefined,
      modelName: form.modelName.trim(),
      imageUrl: form.imageUrl.trim(),
      basePrice,
      launchDate: form.launchDate || null,
      availableStorage: parseStorage(form.storageRows),
      specifications: {},
      availableColors: [],
    };
  };

  const resetForm = () => {
    const keepCategory = searchParams.get("categoryId") || "";
    setForm({ ...emptyForm, categoryId: keepCategory });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");
    setSaving(true);
    try {
      const payload = await buildPayload();
      if (editingId) {
        await updateDevice(editingId, payload);
        setSuccess("Model updated.");
      } else {
        await createDevice(payload);
        setSuccess("Model created.");
      }
      resetForm();
      await loadDevices();
      searchDevices({ query: "", size: 300 })
        .then((d) => setAllDevices(d.content || []))
        .catch(() => {});
    } catch (err) {
      setError(err.message || "Failed to save model");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = async (device) => {
    setFormLoading(true);
    setError("");
    try {
      const full = await fetchDeviceById(device.id);
      setEditingId(full.id);
      setForm({
        brandId: full.brandId != null
          ? String(full.brandId)
          : String(brands.find((b) => b.name === full.brandName)?.id || ""),
        categoryId: full.categoryId != null ? String(full.categoryId) : "",
        seriesId: full.seriesId != null ? String(full.seriesId) : "",
        newSeriesName: "",
        cloneFromDeviceId: "",
        modelName: full.modelName,
        imageUrl: full.imageUrl || "",
        basePrice: String(full.basePrice),
        launchDate: full.launchDate || "",
        storageRows: storageRowsFromApi(full.availableStorage),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const previewAssessment = async (deviceId) => {
    try {
      setAssessmentPreview(await fetchDeviceAssessment(deviceId));
    } catch (err) {
      setError(err.message);
    }
  };

  const confirmClone = async () => {
    if (!cloneTarget || !cloneSourceId) return;
    try {
      await cloneAssessmentToDevice(cloneTarget, parseInt(cloneSourceId, 10));
      setSuccess("Overrides cloned.");
      setCloneTarget(null);
      setCloneSourceId("");
    } catch (err) {
      setError(err.message);
    }
  };

  const categoryName = (id) => categories.find((c) => String(c.id) === String(id))?.name || "-";

  const cloneCandidates = (targetDeviceId, targetBrandId, targetCategoryId) => {
    return allDevices.filter((d) => {
      if (d.id === targetDeviceId) return false;
      const dBrandId = d.brandId ?? brands.find((b) => b.name === d.brandName)?.id;
      if (targetBrandId && String(dBrandId) !== String(targetBrandId)) return false;
      if (targetCategoryId && String(d.categoryId) !== String(targetCategoryId)) return false;
      return true;
    });
  };

  const renderCloneOptions = (targetDeviceId, targetBrandId, targetCategoryId) => {
    const candidates = cloneCandidates(targetDeviceId, targetBrandId, targetCategoryId);
    if (candidates.length === 0) {
      return <option value="" disabled>No same-brand models in this category</option>;
    }
    return candidates.map((d) => (
      <option key={d.id} value={d.id}>{d.brandName} {d.modelName}</option>
    ));
  };

  const formCloneDevices = allDevices.filter((d) => {
    if (!form.categoryId) return false;
    if (String(d.categoryId) !== String(form.categoryId)) return false;
    if (form.brandId && String(d.brandId ?? brands.find((b) => b.name === d.brandName)?.id) !== String(form.brandId)) {
      return false;
    }
    return true;
  });

  return (
    <div className="admin-page">
      <h2>Device models</h2>
      <p className="subtitle">
        Models inherit Q&amp;A and defects from their <strong>product type</strong> (Mobile, Tablet…).
        Set pricing here; use <strong>Defects</strong> on a row to override impact % for one model, or <strong>Clone</strong> to copy overrides from another model.
      </p>

      {error && <p className="error-msg">{error}</p>}
      {success && <p className="success-msg">{success}</p>}

      <div className="admin-card">
        <h3>{editingId ? `Edit model #${editingId}` : "Add model"}</h3>
        {formLoading ? (
          <div className="admin-loading">Loading model</div>
        ) : (
          <form className="admin-form admin-form-grid" onSubmit={handleSubmit}>
            <label className="field-label">
              Brand
              <select value={form.brandId} onChange={(e) => setForm({ ...form, brandId: e.target.value, seriesId: "", newSeriesName: "" })} required>
                <option value="">Select brand</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
            <label className="field-label">
              Product category
              <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value, seriesId: "", newSeriesName: "" })} required>
                <option value="">Select category</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="field-label">
              Series (optional)
              <select
                value={form.seriesId}
                disabled={!form.brandId || !form.categoryId || seriesLoading || !!form.newSeriesName.trim()}
                onChange={(e) => setForm({ ...form, seriesId: e.target.value, newSeriesName: "" })}
              >
                <option value="">No series / standalone model</option>
                {seriesOptions.filter((s) => s.isActive !== false).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.deviceCount != null ? ` (${s.deviceCount})` : ""}</option>
                ))}
              </select>
            </label>
            <label className="field-label span-2">
              Or create new series
              <input
                placeholder="e.g. Galaxy A Series"
                value={form.newSeriesName}
                disabled={!form.brandId || !form.categoryId}
                onChange={(e) => setForm({ ...form, newSeriesName: e.target.value, seriesId: "" })}
              />
            </label>
            <p className="field-hint span-2">
              Group models under a series for the sell flow (Samsung A/J/S series). Leave empty for standalone models like iPhone 14.
            </p>
            {!editingId && (
              <label className="field-label span-2">
                Copy overrides from sibling (optional)
                <select value={form.cloneFromDeviceId} onChange={(e) => setForm({ ...form, cloneFromDeviceId: e.target.value })}>
                  <option value="">None</option>
                  {!form.brandId ? (
                    <option value="" disabled>Select brand first</option>
                  ) : !form.categoryId ? (
                    <option value="" disabled>Select category first</option>
                  ) : formCloneDevices.length > 0 ? (
                    formCloneDevices.map((d) => (
                      <option key={d.id} value={d.id}>{d.modelName}</option>
                    ))
                  ) : (
                    <option value="" disabled>No sibling models in this brand and category</option>
                  )}
                </select>
              </label>
            )}
            <label className="field-label span-2">
              Model name
              <input value={form.modelName} onChange={(e) => setForm({ ...form, modelName: e.target.value })} required />
            </label>
            <label className="field-label span-2">
              Image URL
              <input value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} required />
            </label>
            <label className="field-label">
              Base price (₹)
              <input type="number" value={form.basePrice} onChange={(e) => setForm({ ...form, basePrice: e.target.value })} required />
            </label>
            <label className="field-label">
              Launch date
              <input type="date" value={form.launchDate} onChange={(e) => setForm({ ...form, launchDate: e.target.value })} />
            </label>
            <div className="field-label span-2">
              <p className="field-label" style={{ marginBottom: 8 }}>Storage variants</p>
              <StorageVariantsEditor
                rows={form.storageRows}
                onChange={(storageRows) => setForm({ ...form, storageRows })}
                basePrice={form.basePrice}
              />
            </div>
            <div className="form-actions span-2">
              <button type="submit" className="admin-btn" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Update" : "Create"}
              </button>
              {editingId && (
                <button type="button" className="admin-btn secondary" onClick={resetForm} disabled={saving}>Cancel</button>
              )}
            </div>
          </form>
        )}
      </div>

      <div className="admin-filters">
        <label className="field-label">
          Search
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        </label>
        <label className="field-label">
          Brand
          <select value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setPage(0); }}>
            <option value="">All brands</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label className="field-label">
          Category
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="admin-loading">Loading models</div>
      ) : (
        <>
          <TableScroll>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Brand</th>
                <th>Series</th>
                <th>Category</th>
                <th>Base</th>
                <th>Variants</th>
                <th>Popular</th>
                <th>Status</th>
                <th>Manage</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 && <tr><td colSpan={9} className="empty-msg">No models</td></tr>}
              {devices.map((d) => (
                <tr key={d.id}>
                  <td><strong>{d.modelName}</strong></td>
                  <td>{d.brandName}</td>
                  <td>{d.seriesName || "—"}</td>
                  <td>{categoryName(d.categoryId)}</td>
                  <td>₹{d.basePrice}</td>
                  <td>{formatStorageSummary(d.availableStorage)}</td>
                  <td>{d.isPopular ? "Yes" : "No"}</td>
                  <td className={d.isActive ? "badge-active" : "badge-inactive"}>{d.isActive ? "Active" : "Inactive"}</td>
                  <td className="actions-cell">
                    <button type="button" className="admin-btn secondary" onClick={() => startEdit(d)}>Edit</button>
                    <button type="button" className="admin-btn secondary" onClick={() => previewAssessment(d.id)}>Preview</button>
                    <button type="button" className="admin-btn secondary" title="Question overrides" onClick={() => navigate(`/models/${d.id}/questions`)}>Questions</button>
                    <button type="button" className="admin-btn secondary" title="Defect overrides" onClick={() => navigate(`/models/${d.id}/defects`)}>Defects</button>
                    <button type="button" className="admin-btn secondary" onClick={() => { setCloneTarget(d.id); setCloneSourceId(""); }}>Clone</button>
                    <button type="button" className="admin-btn" onClick={async () => { await toggleDevicePopular(d.id); await loadDevices(); }}>
                      {d.isPopular ? "Unmark popular" : "Mark popular"}
                    </button>
                    {d.isActive && (
                      <button type="button" className="admin-btn danger" onClick={async () => {
                        if (window.confirm("Deactivate this model?")) {
                          await deleteDevice(d.id);
                          await loadDevices();
                        }
                      }}>Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      {assessmentPreview && (
        <ConfirmModal
          title={`Resolved assessment (${assessmentPreview.resolutionMode})`}
          message={`What the mobile sell flow will use for this model.`}
          confirmLabel="Close"
          cancelLabel=""
          onConfirm={() => setAssessmentPreview(null)}
          onCancel={() => setAssessmentPreview(null)}
        >
          <p><strong>{assessmentPreview.questions?.length || 0}</strong> questions</p>
          <ul style={{ fontSize: 13, maxHeight: 120, overflow: "auto" }}>
            {(assessmentPreview.questions || []).slice(0, 8).map((q) => (
              <li key={q.id}>{q.category}: {q.question}</li>
            ))}
          </ul>
          <p><strong>{assessmentPreview.defects?.length || 0}</strong> defects</p>
          <ul style={{ fontSize: 13, maxHeight: 120, overflow: "auto" }}>
            {(assessmentPreview.defects || []).slice(0, 8).map((d) => (
              <li key={d.id}>{d.category}: {d.title}</li>
            ))}
          </ul>
        </ConfirmModal>
      )}

      {cloneTarget && (
        <ConfirmModal
          title="Clone device overrides"
          message="Copy device-level overrides from another model (same brand + category)."
          confirmLabel="Clone"
          onConfirm={confirmClone}
          onCancel={() => { setCloneTarget(null); setCloneSourceId(""); }}
        >
          <label className="field-label">
            Source model
            <select value={cloneSourceId} onChange={(e) => setCloneSourceId(e.target.value)}>
              <option value="">Select source model</option>
            {(() => {
              const target = allDevices.find((d) => d.id === cloneTarget) || devices.find((d) => d.id === cloneTarget);
              const brandId = target?.brandId ?? brands.find((b) => b.name === target?.brandName)?.id;
              return renderCloneOptions(cloneTarget, brandId, target?.categoryId);
            })()}
          </select>
          </label>
        </ConfirmModal>
      )}
    </div>
  );
};

export default Models;
